import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, User, LogOut, ScanLine } from './icons';
import { usePopIn } from '../hooks/usePopIn';
import type { AuthUser } from '../types/api';

interface Props {
  user: AuthUser;
  /** Header 的 handleLogout：内部已 logout() + navigate('/')。 */
  onLogout: () => void;
}

/**
 * 桌面端账户菜单：一个头像按钮，点开下拉收纳「身份行 / 个人中心 / 主题三选一 / 退出」。
 * 取代旧的「绿点 + 长邮箱链接 + 退出按钮」三件横排，给顶栏右侧减负。
 * 仅 >= lg 使用；移动端账户操作仍在 MobileMenu 抽屉内。
 */
export function UserMenu({ user, onLogout }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 下拉弹入：从触发点(右上角)轻微缩放升入；reduce-motion 下不动。
  usePopIn(menuRef, open, { origin: 'top right', y: 6, scale: 0.96, duration: 0.18 });

  const realName = user.realName?.trim() ?? '';
  // 有真实姓名取首字；没有则用 User 图标兜底（纯数字 QQ 邮箱取首字符会变「1」，无意义）。
  const initial = realName ? realName[0] : '';

  // 点菜单外 / 按 Esc 关闭（沿用 HeaderSearch 的同款关闭逻辑）。
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function handleLogout() {
    setOpen(false);
    onLogout();
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={realName || user.email || '未绑定邮箱'}
        className="flex h-9 flex-shrink-0 items-center gap-1 rounded-full pl-1 pr-1.5 text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
      >
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-fill text-[12px] font-medium leading-none text-white"
        >
          {initial || <User className="h-3.5 w-3.5" />}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-60 border border-line bg-surface shadow-sm"
        >
          {/* 身份行 */}
          <div className="flex items-center gap-2.5 px-3 py-3">
            <span
              aria-hidden
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-navy-fill text-[13px] font-medium leading-none text-white"
            >
              {initial || <User className="h-4 w-4" />}
            </span>
            <div className="min-w-0 leading-tight">
              {realName ? (
                <>
                  <p className="truncate text-[13px] text-fg">{realName}</p>
                  <p className="truncate text-[12px] text-fg-subtle">{user.email ?? '未绑定邮箱'}</p>
                </>
              ) : (
                <p className="truncate text-[13px] text-fg">{user.email ?? '未绑定邮箱'}</p>
              )}
            </div>
          </div>

          <div className="border-t border-line" />

          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
          >
            <User className="h-4 w-4 flex-shrink-0" />
            个人中心
          </Link>

          <Link
            to="/scan-login"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg"
          >
            <ScanLine className="h-4 w-4 flex-shrink-0" />
            扫码登录电脑
          </Link>

          <div className="border-t border-line" />

          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-fg-muted transition-colors hover:bg-surface-muted hover:text-danger"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            退出
          </button>
        </div>
      )}
    </div>
  );
}
